import React, { createContext, useContext, useState, useEffect } from "react";
import { authService, registerTokenGetter } from "../services/api";

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  login: (password: string, googleToken: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean; // new
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // new

  const isAuthenticated = !!accessToken;

  const login = async (password: string, googleToken: string) => {
    const res = await authService.login(password, googleToken);
    setAccessToken(res.data.accessToken);
    registerTokenGetter(() => res.data.accessToken);
  };

  const logout = async () => {
    await authService.logout();
    setAccessToken(null);
  };

  // Refresh token on mount
  useEffect(() => {
    registerTokenGetter(() => accessToken);

    const tryRefresh = async () => {
      try {
        const res = await authService.refresh();
        setAccessToken(res.data.accessToken);
        registerTokenGetter(() => res.data.accessToken);
      } catch {
        setAccessToken(null);
      } finally {
        setLoading(false); // done refreshing
      }
    };

    tryRefresh();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, accessToken, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};